import {createAction} from 'Shared/utilities/actionCreator';
import * as V from 'ApplicationEntry/models/VerifyCriticalInformation';
import * as G from 'ApplicationEntry/models/GroupVerifyCriticalInformation';
import * as NY from 'ApplicationEntry/models/NewYorkVerifyCriticalInformation';
import * as O from 'ApplicationEntry/models/OrdinaryVerifyCriticalInformation';
import {reverseEnum} from 'Shared/utilities/enumUtility';
import {Async} from 'Core/models/Async';
import {AgentList} from 'ApplicationEntry/models/AgentList';

export enum VerifyCriticalInformationActionType {
  CHANGE_CASE_NUMBER_ERROR = 'verifyCriticalInformation/CHANGE_CASE_NUMBER_ERROR',
  CHANGE_CASE_NUMBER = 'verifyCriticalInformation/CHANGE_CASE_NUMBER',
  CHANGE_USER_CASE_NUMBER = 'verifyCriticalInformation/CHANGE_USER_CASE_NUMBER',
  CHANGE_CASE_NAME = 'verifyCriticalInformation/CHANGE_CASE_NAME',
  CHANGE_APPLICANT = 'verifyCriticalInformation/CHANGE_APPLICANT',
  CHANGE_TRANSIT = 'verifyCriticalInformation/CHANGE_TRANSIT',
  CHANGE_CONTROL_NUMBER = 'verifyCriticalInformation/CHANGE_CONTROL_NUMBER',
  CHANGE_ACCOUNT_NUMBER = 'verifyCriticalInformation/CHANGE_ACCOUNT_NUMBER',
  GET_CASE_NUMBER_MESSAGE = 'verifyCriticalInformation/GET_CASE_NUMBER_MESSAGE',
  GET_CASE_NAME = 'verifyCriticalInformation/GET_CASE_NAME',
  CLEAR_CASE_NUMBER = 'verifyCriticalInformation/GET_CASE_NUMBER',
  CHANGE_AGENT_LIST = 'verifyCriticalInformation/CHANGE_AGENT_LIST',
  CLEAR_INFORMATION = 'verifyCriticalInformation/CLEAR_INFORMATION',
  GET_ACH_TRANSIT = 'verifyCriticalInformation/GET_ACH_TRANSIT',
  GET_ACH_ACCOUNT_NUMBER = 'verifyCriticalInformation/GET_ACH_ACCOUNT_NUMBER',
}

export const isVerifyCriticalInformationAction = (action: any): action is VerifyCriticalInformationAction =>
  action.type in reverseEnum(VerifyCriticalInformationActionType);

export const changeCaseName = (caseName: string) =>
  createAction(VerifyCriticalInformationActionType.CHANGE_CASE_NAME, {caseName});
export const changeUserCaseNumber = (caseNumber: string) =>
  createAction(VerifyCriticalInformationActionType.CHANGE_USER_CASE_NUMBER, {caseNumber});
export const changeCaseNumber = (caseNumber: Async<string>) =>
  createAction(VerifyCriticalInformationActionType.CHANGE_CASE_NUMBER, {caseNumber});
export const changeCaseNumberError = (caseNumber: string) =>
  createAction(VerifyCriticalInformationActionType.GET_CASE_NUMBER_MESSAGE, {caseNumber});
export const getCaseNameByCaseNumber = (caseNumber: string) =>
  createAction(VerifyCriticalInformationActionType.GET_CASE_NAME, {caseNumber});
export const clearCaseNumber = () => createAction(VerifyCriticalInformationActionType.CLEAR_CASE_NUMBER);
export const clearInformation = () => createAction(VerifyCriticalInformationActionType.CLEAR_INFORMATION);
export const changeTransit = (transit: string) =>
  createAction(VerifyCriticalInformationActionType.CHANGE_TRANSIT, {transit});
export const changeAccountNumber = (accountNumber: string) =>
  createAction(VerifyCriticalInformationActionType.CHANGE_ACCOUNT_NUMBER, {accountNumber});
export const changeControlNumber = (controlNumber: string) =>
  createAction(VerifyCriticalInformationActionType.CHANGE_CONTROL_NUMBER, {controlNumber});
export const changeApplicant = (applicant: string) =>
  createAction(VerifyCriticalInformationActionType.CHANGE_APPLICANT, {applicant});
export const changeAgentList = (agentList: AgentList) =>
  createAction(VerifyCriticalInformationActionType.CHANGE_AGENT_LIST, {agentList});
export const getAchTransitNumber = (achTransitNumber: string) => 
  createAction(VerifyCriticalInformationActionType.GET_ACH_TRANSIT, {achTransitNumber});
export const getAchAccountNumber = (achAccountNumber: string) => 
  createAction(VerifyCriticalInformationActionType.GET_ACH_ACCOUNT_NUMBER, {achAccountNumber});

export type VerifyCriticalInformationAction =
  | ReturnType<typeof changeUserCaseNumber>
  | ReturnType<typeof changeCaseNumber>
  | ReturnType<typeof changeCaseName>
  | ReturnType<typeof changeCaseNumberError>
  | ReturnType<typeof clearCaseNumber>
  | ReturnType<typeof clearInformation>
  | ReturnType<typeof changeControlNumber>
  | ReturnType<typeof changeTransit>
  | ReturnType<typeof changeAccountNumber>
  | ReturnType<typeof changeApplicant>
  | ReturnType<typeof changeAgentList>
  | ReturnType<typeof getAchAccountNumber>
  | ReturnType<typeof getAchTransitNumber>;

type VerifyCriticalInformationState = V.VerifyCriticalInformation;

export default (
  state: VerifyCriticalInformationState,
  action: VerifyCriticalInformationAction
): VerifyCriticalInformationState => {
  switch (action.type) {
    case VerifyCriticalInformationActionType.GET_CASE_NUMBER_MESSAGE:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Ordinary: O.changeCaseNumberError(action.payload.caseNumber),
        Group: G.changeCaseNumberError(action.payload.caseNumber),
        NewYork: NY.changeCaseNumberError(action.payload.caseNumber),
      })(state);
    case VerifyCriticalInformationActionType.CHANGE_CASE_NAME:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Ordinary: O.changeCaseName(action.payload.caseName),
        Group: G.changeCaseName(action.payload.caseName),
        NewYork: NY.changeCaseName(action.payload.caseName),
      })(state);
    case VerifyCriticalInformationActionType.CHANGE_CASE_NUMBER:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Ordinary: O.changeCaseNumber(action.payload.caseNumber),
        Group: G.changeCaseNumber(action.payload.caseNumber),
        NewYork: NY.changeCaseNumber(action.payload.caseNumber),
      })(state);
    case VerifyCriticalInformationActionType.CHANGE_USER_CASE_NUMBER:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Ordinary: O.changeUserCaseNumber(action.payload.caseNumber),
        Group: G.changeUserCaseNumber(action.payload.caseNumber),
        NewYork: NY.changeUserCaseNumber(action.payload.caseNumber),
      })(state);
    case VerifyCriticalInformationActionType.CLEAR_CASE_NUMBER:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Ordinary: O.changeUserCaseNumber(''),
        Group: G.changeUserCaseNumber(''),
        NewYork: NY.changeUserCaseNumber(''),
      })(state);
    case VerifyCriticalInformationActionType.CLEAR_INFORMATION:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Ordinary: () => O.EmptyOrdinaryVerifyCriticalInformation,
        Group: () => G.EmptyGroupVerifyCriticalInformation,
        NewYork: () => NY.EmptyNewYorkVerifyCriticalInformation,
      })(state);
    case VerifyCriticalInformationActionType.CHANGE_CONTROL_NUMBER:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Ordinary: O.changeControlNumber(action.payload.controlNumber),
        Group: G.changeControlNumber(action.payload.controlNumber),
        NewYork: NY.changeControlNumber(action.payload.controlNumber),
      })(state);
    case VerifyCriticalInformationActionType.CHANGE_TRANSIT:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Ordinary: O.changeTransit(action.payload.transit),
        NewYork: NY.changeTransit(action.payload.transit),
        _: () => state,
      })(state);
    case VerifyCriticalInformationActionType.CHANGE_ACCOUNT_NUMBER:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Ordinary: O.changeAccountNumber(action.payload.accountNumber),
        NewYork: NY.changeAccountNumber(action.payload.accountNumber),
        _: () => state,
      })(state);
    case VerifyCriticalInformationActionType.CHANGE_APPLICANT:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Ordinary: O.changeApplicant(action.payload.applicant),
        Group: G.changeApplicant(action.payload.applicant),
        NewYork: NY.changeApplicant(action.payload.applicant),
      })(state);
    case VerifyCriticalInformationActionType.CHANGE_AGENT_LIST:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Ordinary: O.changeAgentList(action.payload.agentList),
        Group: G.changeAgentList(action.payload.agentList),
        NewYork: NY.changeAgentList(action.payload.agentList),
      })(state);
    case VerifyCriticalInformationActionType.GET_ACH_ACCOUNT_NUMBER:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Group: G.changeAchAccountNumber(action.payload.achAccountNumber),
        _: () => state,
      })(state);
    case VerifyCriticalInformationActionType.GET_ACH_TRANSIT:
      return V.matchVerifyCriticalInformationByType<V.VerifyCriticalInformation>({
        Group: G.changeAchTransitNumber(action.payload.achTransitNumber),
        _: () => state,
      })(state);
    default:
      return state;
  }
};
